import { ApiACLRole } from "@incendium/api";
import { initializeClientsThunk } from "Actions";
import Loading from "Components/Loading/Loading";
import { useUser } from "Hooks/useUser";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useLocation } from "react-router-dom";
import { clientListSelector } from "Selectors/clientListSelector";
import { IStoreState } from "Interfaces/interfaces";

function extractProjectIdFromPath(pathname: string): number | null {
  const match = pathname.match(/\/projects\/(\d+)/);
  return match ? Number(match[1]) : null;
}

function findFirstPermission(
  user: any,
  projectId: number | null,
  currentEnv: string | null
) {
  const permissionsForEnv = (user.permissions || []).filter(
    (p: any) => (p.environment ?? null) === (currentEnv ?? null)
  );
  if (projectId !== null) {
    const foundPermission = permissionsForEnv.find(
      (p: any) => p.projectId === projectId
    );
    if (foundPermission) {
      return foundPermission;
    }
  }
  return permissionsForEnv[0];
}

function findClient(state: any, firstPermission: any) {
  if (!state.hasLoaded) {
    return null;
  }
  return state.list.find(
    (c) =>
      c.id === firstPermission.clientId ||
      (c.projects || []).some((p) => p.id === firstPermission.projectId)
  );
}

function ProjectAnalysisCatch() {
  const { user } = useUser();
  const location = useLocation();
  const dispatch = useDispatch();
  const state = useSelector(clientListSelector);
  const currentEnv = useSelector(
    (s: IStoreState) => s.auth.tokens?.selectedEnv ?? null
  );

  useEffect(() => {
    if (!state.hasLoaded) {
      dispatch(initializeClientsThunk());
    }
  }, [dispatch, state]);

  const firstPermission = useMemo(() => {
    const projectId = extractProjectIdFromPath(location.pathname);
    return findFirstPermission(user, projectId, currentEnv);
  }, [user, location, currentEnv]);

  const client = useMemo(() => {
    if (!firstPermission || !state.hasLoaded) {
      return null;
    }
    return findClient(state, firstPermission);
  }, [state, firstPermission]);

  if (!state.hasLoaded || !client) {
    return <Loading fullHeight />;
  }

  if (
    [ApiACLRole.CLIENT_ADMIN, ApiACLRole.CLIENT_OBSERVER].includes(
      firstPermission.role!
    )
  ) {
    return <Redirect to={`/clients/${client.id}`} />;
  }

  if (
    [ApiACLRole.PROJECT_ADMIN, ApiACLRole.PROJECT_OBSERVER].includes(
      firstPermission.role!
    )
  ) {
    return (
      <Redirect
        to={`/clients/${client.id}/projects/${firstPermission.projectId}`}
      />
    );
  }

  return (
    <Redirect
      to={`/clients/${client.id}/projects/${firstPermission.projectId}/analyse`}
    />
  );
}

export default ProjectAnalysisCatch;
